
import Vue, { PropType } from 'vue';
import BaseWelcomeStepPage from './BaseWelcomeStepPage.vue';
import Select, { SelectOption } from '~/components/builders/Base/Select.vue';
import YearMonthPicker from '~/components/builders/YearMonthPicker.vue';

import CSPService, {
    CandidateSchool,
    CandidateStudyProgram,
    StudyField,
    StudyLevel,
    StudyProgram,
} from '~/services/CSPService';
import CandidateService from '~/services/CandidateService';
import { commonProps, setupBuilder } from '~/utils/candidate';
import { CandidateJobBuilder, JobCSP } from '~/utils/candidate';
import { CandidateStudyProgramHelper } from '~/utils/candidate/CandidateStudyProgramHelper';
import cloneDeep from 'lodash/cloneDeep';

export default Vue.extend({
    name: 'WelcomeStep2Page',
    components: {
        BaseWelcomeStepPage,
        Select,
        YearMonthPicker,
    },
    props: {
        pageNumber: { type: Number, required: true },
        pageCount: { type: Number, required: true },
        clickBack: {
            type: Function,
            default: () => {},
        },
        clickNext: {
            type: Function,
            default: () => {},
        },
        error: {
            type: Function,
            default: () => {},
        },

        ...commonProps(),
    },
    data() {
        return {
            builder: null as CandidateJobBuilder | null,
            service: new CandidateService(),
            cspService: new CSPService(),

            needInitCSP: true,

            csp: null as JobCSP | null,

            school: '',
            program: undefined as string | undefined,
            campus: '',
            level: undefined as string | undefined,
            field: '',
            start: null as Date | null,
            end: null as Date | null,

            customSchool: false,
            customProgram: false,
            newProgram: false,

            programs: [] as JobCSP[],

            schools: [] as CandidateSchool[],
            levels: [] as StudyLevel[],
            fields: [] as StudyField[],
            schoolStudyPrograms: new Map<string, StudyProgram[]>(),

            isLoading: false,

            unwatchBuilder: undefined as (() => void) | undefined,
        };
    },
    computed: {
        disabled(): Record<string, boolean> {
            const schoolId = Number(this.school);
            const schoolSelected =
                (this.customSchool && !!this.school) || this.schools.some(s => s.id === schoolId);

            return {
                program: !schoolSelected,
                campus: !schoolSelected || this.optionsCampuses.length < 2,
                level: !schoolSelected || !this.customProgram,
                field: !schoolSelected || !this.customProgram,
                dates: !schoolSelected,
            };
        },
        optionsSchools(): SelectOption[] {
            const options: SelectOption[] = this.schools.map(school => ({
                value: String(school.id),
                label: school.name,
            }));

            if (this.customSchool)
                options.unshift({
                    value: this.school,
                    label: this.school,
                    class: 'text-muted',
                    suffix: this.$t('profile.studyPrograms.customInput').toString(),
                    isCustom: true,
                });

            return options;
        },
        optionsStudyPrograms(): SelectOption[] {
            if (this.customSchool)
                return this.customProgram && !!this.program
                    ? [{ value: this.program, label: this.program }]
                    : [];

            const options: SelectOption[] = (this.schoolStudyPrograms.get(this.school) || []).map(
                program => ({
                    value: program.id,
                    label: program.level
                        ? `${program.name} (${program.level.name})`
                        : program.name || '',
                })
            );

            if (this.customProgram && !!this.program)
                options.unshift({
                    value: this.program,
                    label: this.program,
                    isCustom: true,
                });
            else if (
                !!this.csp &&
                !!this.csp.studyProgram &&
                !this.csp.studyProgram.isVerified &&
                !!this.csp.studyProgram.name
            )
                options.unshift({
                    value: this.csp.studyProgram.name,
                    label: this.csp.studyProgram.name,
                    isCustom: true,
                });

            return options;
        },
        optionsCampuses(): SelectOption[] {
            if (this.customSchool || this.customProgram || !this.program) return [];

            const ssps = this.schoolStudyPrograms.get(this.school);
            if (!ssps) return [];

            const program = ssps.find(s => s.id === this.program);
            if (!program || !program.campuses) return [];

            return program.campuses.map(c => ({
                value: c.id,
                label: c.name,
            }));
        },
        optionsLevels(): SelectOption[] {
            return this.levels.map(level => ({
                value: level.id,
                label: level.name,
            }));
        },
        optionsFields(): SelectOption[] {
            return this.fields.map(field => ({
                value: field.id,
                label: field.name,
            }));
        },

        existingPrograms(): CandidateStudyProgram[] {
            return this.$store.getters['profileModule/getSortedStudyPrograms']();
        },
        isTimeValid(): boolean | null {
            if (this.start && this.end) return this.start <= this.end;
            return true;
        },
        isFormValid(): boolean {
            return Boolean(
                this.school &&
                    (!this.customProgram || (this.level && this.field)) &&
                    this.end &&
                    this.isTimeValid
            );
        },
    },
    methods: {
        handleClickBack() {
            this.$emit('clickBack');
        },
        handleClickNext() {
            this.updatePrograms();

            this.$nextTick(() => {
                if (!this.builder || this.builder.isEmpty()) {
                    this.needInitCSP = true;
                    console.log('builder is empty');
                    this.$emit('clickNext');
                    return;
                }

                const request = this.builder.buildRequest();

                this.isLoading = true;
                this.service
                    .updateCandidateProfile(request)
                    .then(() => {
                        this.isLoading = false;
                        this.needInitCSP = true;
                        this.$emit('clickNext');
                    })
                    .catch(err => {
                        this.$emit('error', err);
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });

                this.builder.reset();
            });
        },

        async loadSchools() {
            const schools = await this.cspService.getSchools();
            this.schools = schools.sort((a, b) => a.name.localeCompare(b.name));
        },
        async loadLevels() {
            this.levels = await this.cspService.getStudyLevels();
        },
        async loadFields() {
            this.fields = await this.cspService.getStudyFields();
        },
        async loadSchoolStudyPrograms(id: string) {
            if (isNaN(Number(id))) return;
            if (this.schoolStudyPrograms.has(id)) return;

            const programs = await this.cspService.getSchoolStudyPrograms(Number(id));
            this.setSchoolStudyPrograms(id, programs || []);
        },
        setSchoolStudyPrograms(schoolId: string, programs: StudyProgram[]) {
            this.schoolStudyPrograms = new Map(this.schoolStudyPrograms.set(schoolId, programs));
        },

        handleSchoolChange(value: string, custom: boolean) {
            this.school = value;
            this.customSchool = custom;

            if (custom) {
                this.customProgram = true;
                this.program = '';
            } else {
                this.loadSchoolStudyPrograms(value);
            }
        },
        handleProgramChange(value: string, custom: boolean, options: SelectOption[]) {
            this.program = value;
            this.customProgram = custom;
            this.newProgram = custom && options.every(o => o.value !== value);

            if (!custom && !this.customSchool && this.school) {
                this.$nextTick(() => {
                    if (!!this.csp && value === this.csp.programId && !!this.csp.campusId) {
                        this.campus = this.csp.campusId;
                        return;
                    }

                    const ssps = this.schoolStudyPrograms.get(this.school);
                    if (!ssps) return;

                    const program = ssps.find(s => s.id === this.program);
                    if (!program) return;

                    this.level = program.level ? program.level.id : '';
                    this.field =
                        program.fields && program.fields.length ? program.fields[0].id : '';
                    this.campus =
                        program.campuses && program.campuses.length ? program.campuses[0].id : '';
                });
            }
        },
        getSchoolStudyProgram(programId: string, schoolId?: string | number) {
            if (typeof schoolId === 'undefined') {
                for (const [, programs] of this.schoolStudyPrograms) {
                    const program = programs.find(p => p.id === programId);
                    if (!!program) return program;
                }

                return null;
            } else {
                if (isNaN(Number(schoolId))) return null;

                const programs = this.schoolStudyPrograms.get(schoolId + '');
                if (!programs) return null;

                return programs.find(p => p.id === programId) || null;
            }
        },
        initCSP() {
            if (!this.existingPrograms.length) {
                this.csp = this.initNewCSP();
                return;
            }

            let existingProgram: CandidateStudyProgram | undefined = undefined;
            let jobCSP: JobCSP | undefined = undefined;

            // Select the education of most relevance
            const schoolId = CandidateStudyProgramHelper.calculateSchoolIdOfMostRelevance(
                this.existingPrograms
            );
            if (schoolId) {
                existingProgram = this.existingPrograms.find(
                    p => p.studyProgram?.schoolId === schoolId
                );
                jobCSP = this.programs.find(p => p.studyProgram?.schoolId === schoolId);
            } else {
                existingProgram = this.existingPrograms[0];
                jobCSP = this.programs.find(p => p.id === existingProgram?.id);
            }

            if (existingProgram && existingProgram.studyProgram && jobCSP) {
                this.initExistingCSP(existingProgram);
                this.csp = jobCSP;
            } else {
                this.csp = this.initNewCSP();
            }
        },
        initExistingCSP(csp: CandidateStudyProgram) {
            const field = csp.studyProgram.fields ? csp.studyProgram.fields[0] : null;
            const campus = !!csp.campusId
                ? csp.campusId
                : !!this.optionsCampuses.length
                ? this.optionsCampuses[0].value
                : '';

            this.school =
                csp.studyProgram.customSchoolName || String(csp.studyProgram.schoolId || -1);
            this.program = csp.studyProgram.isVerified
                ? csp.programId || ''
                : csp.studyProgram.name;
            this.campus = campus;
            this.level = csp.studyProgram.levelId;
            this.field = field ? field.id : '';
            this.start = csp.start;
            this.end = csp.end;

            this.customSchool = !!csp.studyProgram.customSchoolName;
            this.customProgram = !csp.studyProgram.isVerified;
            this.newProgram = false;

            if (csp.studyProgram.schoolId)
                this.loadSchoolStudyPrograms(csp.studyProgram.schoolId + '');
        },
        initNewCSP(): JobCSP {
            const campus = !!this.optionsCampuses.length ? this.optionsCampuses[0].value : '';

            this.school = String(-1);
            this.program = '';
            this.campus = campus;
            this.level = '';
            this.field = '';
            this.start = null;
            this.end = null;

            this.customSchool = false;
            this.customProgram = true;
            this.newProgram = false;

            const newCSP = {
                id: CandidateJobBuilder.generateId(),
                programId: '',
                campusId: undefined,
                studyProgram: {
                    id: '',
                    name: '',
                    schoolId: null,
                    customSchoolName: null,
                    levelId: '',
                    isVerified: false,
                    fields: [],
                },
            } as unknown as JobCSP;

            this.programs.push(newCSP);

            return newCSP;
        },

        updatePrograms() {
            const currentPrograms = cloneDeep(this.programs);

            const idx = currentPrograms.findIndex(p => this.csp && p.id === this.csp.id);
            if (idx < 0) return;

            const prog = currentPrograms[idx];
            prog.start = this.start;
            prog.end = this.end;

            if (!this.customSchool && !this.customProgram) {
                delete prog.studyProgram;
                prog.programId = this.program;

                if (!!this.campus) prog.campusId = this.campus;
            } else {
                prog.campusId = null;
                if (this.customSchool)
                    prog.studyProgram = {
                        name: this.program,
                        levelId: this.level,
                        fields: [],
                        ...prog.studyProgram,
                        schoolId: null,
                        customSchoolName: this.school,
                        isVerified: false,
                    };
                if (this.customProgram) {
                    prog.studyProgram = {
                        schoolId: prog.studyProgram ? prog.studyProgram.schoolId || null : null,
                        customSchoolName: prog.studyProgram
                            ? prog.studyProgram.customSchoolName || null
                            : null,
                        fields: [],
                        ...prog.studyProgram,
                        name: this.program,
                        levelId: this.level,
                        isVerified: false,
                    };

                    if (this.newProgram) {
                        prog.programId = undefined;
                        prog.studyProgram.id = undefined;
                        prog.studyProgram.isVerified = false;
                    }

                    const field = this.fields.find(f => f.id === this.field);
                    prog.studyProgram.fields = !!field ? [{ ...field }] : [];

                    if (!this.customSchool && !!this.school) {
                        prog.studyProgram.schoolId = Number(this.school);
                        prog.studyProgram.customSchoolName = null;
                    }
                }
            }

            if (CandidateJobBuilder.isTempVerificationProgramItem(prog)) {
                prog.id = CandidateJobBuilder.generateCommittedVerificationProgramId();
            }

            currentPrograms.splice(idx, 1, prog);
            this.programs = currentPrograms;
        },
        refreshBuilder() {
            if (this.unwatchBuilder) {
                this.unwatchBuilder();
                this.unwatchBuilder = undefined;
            }

            this.builder = setupBuilder.apply(this);
            this.programs = this.builder.getCSPs();
            this.unwatchBuilder = this.$watch('programs', this.builder.watchCSPs());
        },
    },
    mounted() {
        this.refreshBuilder();

        Promise.all([this.loadSchools(), this.loadLevels(), this.loadFields()]).catch(err => {
            this.$emit('error', err);
        });
    },
    activated() {
        if (this.needInitCSP) {
            this.refreshBuilder();
            this.initCSP();
            this.needInitCSP = false;
        }
    },
});
